import PropTypes from 'prop-types'
import clsx from 'clsx'

import styles from './style.module.scss'

/**
 * Wie weit das Skills4Retail-Konzept ist — als Zahl und als Balken.
 *
 * **Die Zahl wird nicht hier gerechnet.** Sie kommt aus
 * `services/concept/progress.js` und steht damit in der Mitteilung, in der
 * Ansicht der Schüler:innen, in der der Lehrkraft und in der der Verwaltung
 * gleich. Rechnete das Frontend selbst, gäbe es zwei Definitionen — und die
 * Gratulation in der Mitteilungsliste widerspräche dem Balken darunter.
 *
 * **Warum der Balken vier Stücke hat.** Eine nackte Prozentzahl sagt nicht,
 * woran es hängt: „62 %" kann bedeuten, dass die halbe Klasse die Lernstrecke
 * noch offen hat, oder dass zwei Textfelder leer sind. Die vier Stücke sind
 * die vier Abschnitte, die die Checkliste ohnehin nennt, jedes für sich
 * gefüllt.
 *
 * Fehlt der Stand, zeigt die Komponente nichts. Ein Balken auf null wäre eine
 * Aussage, die niemand geprüft hat.
 *
 * @param progress  `{ percent, stages }` aus der Antwort des Servers.
 * @param compact   Für die Mitteilungskarte: nur Zahl und Balken, keine
 *                  Beschriftung unter den Stücken.
 */
export default function ConceptProgress({ progress, compact = false, className }) {
	if (!progress || typeof progress.percent !== 'number') return null

	const stages = progress.stages || []
	if (!stages.length) return null

	return (
		<div className={clsx(styles.root, compact && styles.compact, className)}>
			<div className={styles.head}>
				<span className={styles.label}>Konzept</span>
				<b className={clsx(styles.percent, progress.percent === 100 && styles.done)}>{progress.percent} %</b>
			</div>

			{/*
			 * Die Stücke sind gleich breit, weil sie gleich zählen — die
			 * Gewichtung steckt in der Rechnung und soll hier nicht durch
			 * unterschiedliche Breiten noch einmal behauptet werden.
			 */}
			<div className={styles.rail} role="img" aria-label={`Konzept zu ${progress.percent} Prozent fertig`}>
				{stages.map(stage => (
					<div key={stage.key} className={styles.segment} title={`${stage.label}: ${stage.percent} %`}>
						<div
							className={clsx(styles.fill, stage.complete && styles.full)}
							style={{ width: `${stage.percent}%` }}
						/>
					</div>
				))}
			</div>

			{!compact && (
				<div className={styles.legend}>
					{stages.map(stage => (
						<span key={stage.key} className={clsx(styles.item, stage.complete && styles.itemDone)}>
							{stage.label}
							{/*
							 * „Bewertung" hat als Nenner die 1 — „1 von 1" wäre eine
							 * Zahl ohne Aussage. Die anderen drei zählen echte Dinge.
							 */}
							{stage.total > 1 && (
								<span className={styles.count}>
									{' '}
									{stage.done}/{stage.total}
								</span>
							)}
						</span>
					))}
				</div>
			)}
		</div>
	)
}

ConceptProgress.propTypes = {
	progress: PropTypes.shape({
		percent: PropTypes.number,
		stages: PropTypes.arrayOf(
			PropTypes.shape({
				key: PropTypes.string,
				label: PropTypes.string,
				done: PropTypes.number,
				total: PropTypes.number,
				percent: PropTypes.number,
				complete: PropTypes.bool
			})
		)
	}),
	compact: PropTypes.bool,
	className: PropTypes.string
}
